import React, { useCallback, useEffect, useRef, useState } from "react";

import { clampSize, useDragResize } from "./useDragResize";

function storageKey(panelId, farmId) {
  return `farm-map-floating-panel-${panelId}-${farmId}`;
}

function sanitizeLayout(rawLayout, defaults, bounds) {
  const width = clampSize(
    Number.isFinite(rawLayout.width) ? rawLayout.width : defaults.width,
    220,
    bounds ? Math.min(720, bounds.width - 16) : 720
  );
  const height = clampSize(
    Number.isFinite(rawLayout.height) ? rawLayout.height : defaults.height,
    160,
    bounds ? Math.min(520, bounds.height - 16) : 520
  );
  const maxX = bounds ? Math.max(8, bounds.width - width - 8) : 9999;
  const maxY = bounds ? Math.max(8, bounds.height - height - 8) : 9999;
  const x = clampSize(
    Number.isFinite(rawLayout.x) ? rawLayout.x : defaults.x,
    8,
    maxX
  );
  const y = clampSize(
    Number.isFinite(rawLayout.y) ? rawLayout.y : defaults.y,
    8,
    maxY
  );
  return { x, y, width, height };
}

function readLayout(panelId, farmId, defaults, bounds) {
  try {
    const raw = localStorage.getItem(storageKey(panelId, farmId));
    if (!raw) return sanitizeLayout(defaults, defaults, bounds);
    const parsed = JSON.parse(raw);
    return sanitizeLayout(parsed, defaults, bounds);
  } catch (error) {
    return sanitizeLayout(defaults, defaults, bounds);
  }
}

function writeLayout(panelId, farmId, layout) {
  localStorage.setItem(storageKey(panelId, farmId), JSON.stringify(layout));
}

export default function FarmMapFloatingPanel({
  panelId,
  farmId,
  title,
  children,
  defaultLayout,
}) {
  const panelRef = useRef(null);
  const [bounds, setBounds] = useState(null);
  const [layout, setLayout] = useState(() =>
    sanitizeLayout(defaultLayout, defaultLayout, null)
  );

  useEffect(() => {
    const container = panelRef.current && panelRef.current.offsetParent;
    if (!container) return undefined;

    function updateBounds() {
      setBounds({
        width: container.clientWidth,
        height: container.clientHeight,
      });
    }

    updateBounds();
    window.addEventListener("resize", updateBounds);
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateBounds);
      observer.observe(container);
    }
    return () => {
      window.removeEventListener("resize", updateBounds);
      if (observer) observer.disconnect();
    };
  }, [panelId, farmId]);

  useEffect(() => {
    setLayout(readLayout(panelId, farmId, defaultLayout, bounds));
  }, [panelId, farmId, defaultLayout, bounds]);

  useEffect(() => {
    writeLayout(panelId, farmId, layout);
  }, [panelId, farmId, layout]);

  const handlePanelResize = useCallback(
    (axis, dx, dy) => {
      if (axis === "move") {
        setLayout((current) =>
          sanitizeLayout(
            {
              ...current,
              x: current.x + dx,
              y: current.y + dy,
            },
            defaultLayout,
            bounds
          )
        );
        return;
      }

      if (axis === "resize") {
        setLayout((current) =>
          sanitizeLayout(
            {
              ...current,
              width: current.width + dx,
              height: current.height + dy,
            },
            defaultLayout,
            bounds
          )
        );
      }
    },
    [bounds, defaultLayout]
  );

  const { onResizeStart } = useDragResize(handlePanelResize);

  return (
    <div
      ref={panelRef}
      className="farm-map-floating-panel"
      style={{
        left: layout.x,
        top: layout.y,
        width: layout.width,
        height: layout.height,
      }}
    >
      <div
        className="farm-map-floating-panel__header"
        onMouseDown={(event) => onResizeStart("move", event)}
        title={title}
      >
        <span>{title}</span>
      </div>
      <div className="farm-map-floating-panel__body">{children}</div>
      <div
        className="farm-map-floating-panel__resize"
        title="Arraste para redimensionar"
        onMouseDown={(event) => onResizeStart("resize", event)}
      />
    </div>
  );
}
